import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CalendarDays } from 'lucide-react';
import { PitcherStartInfo } from '@/hooks/useProbablePitchers';
import { format, parseISO } from 'date-fns';

interface ProbablePitcherBadgeProps {
  startInfo: PitcherStartInfo | null;
  compact?: boolean;
}

const dayAbbr = (dateStr: string) => {
  try {
    return format(parseISO(dateStr), 'EEE');
  } catch {
    return dateStr;
  }
};

/**
 * Shows next-start date and two-start indicator for a pitcher.
 * - "📅 vs NYY Thu" for next start
 * - "2️⃣ Two-Start" badge if 2+ starts this week
 */
export default function ProbablePitcherBadge({ startInfo, compact = false }: ProbablePitcherBadgeProps) {
  if (!startInfo) return null;

  const { nextStart, isTwoStart, starts } = startInfo;

  return (
    <TooltipProvider>
      <div className="flex items-center gap-1 flex-wrap">
        {/* Two-Start badge */}
        {isTwoStart && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-[10px] px-1.5 py-0 font-bold gap-0.5"
              >
                2️⃣ {!compact && 'Two-Start'}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-64">
              <p className="text-xs font-medium mb-1">Two-Start Pitcher This Week</p>
              <div className="space-y-0.5">
                {starts.map((s, i) => (
                  <p key={i} className="text-xs text-muted-foreground">
                    {dayAbbr(s.game_date)}, {s.home_away === 'home' ? 'vs' : '@'} {s.opponent_abbreviation}
                  </p>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Next start indicator */}
        {nextStart && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                <CalendarDays className="h-3 w-3" />
                {compact ? (
                  <span>{dayAbbr(nextStart.game_date)}</span>
                ) : (
                  <span>
                    {nextStart.home_away === 'home' ? 'vs' : '@'}{' '}
                    {nextStart.opponent_abbreviation} {dayAbbr(nextStart.game_date)}
                  </span>
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">
                Next Start: {nextStart.home_away === 'home' ? 'vs' : '@'}{' '}
                {nextStart.opponent_abbreviation}, {format(parseISO(nextStart.game_date), 'MMM d')}
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
